<template>
  <div>
    <div class="content-header row"></div>
    <div class="content-body">
      <section id="basic-datatable">
        <div class="row">
          <div class="col-12">
            <div class="card">
              <div class="card-content">
                <div class="card-header">
                  <h5 class="card-title">Manage Templates</h5>
                  <div class="heading-elements">
                    <ul class="list-inline mb-0">
                      <li>
                        <button
                          class="btn btn-primary"
                          @click="createContractModal()"
                        >Create Template</button>
                      </li>
                      <li v-if="isSuperadmin">
                        <div class="has-icon-left">
                          <select
                            class="form-control"
                            v-model="countryFilter"
                            @change="filterByCountry(countryFilter)"
                          >
                            <option selected>All Countries...</option>
                            <option
                              v-for="(country, index) in usersCountries"
                              :value="country"
                              :key="index"
                            >{{ countryName(country) }}</option>
                          </select>
                          <div class="form-control-position">
                            <em class="bx bx-map font-medium-1"></em>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div class="has-icon-left">
                          <select
                            class="form-control"
                            v-model="statusFilter"
                            @change="filterByStatus(statusFilter)"
                          >
                            <option selected>All Status...</option>
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                            <option value="archived">Archived</option>
                          </select>
                          <div class="form-control-position">
                            <em class="bx bx-filter font-medium-1"></em>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="card-body card-dashboard">
                  <div class="text-center">
                    <div
                      class="spinner-border text-primary"
                      role="status"
                      v-if="loadingData"
                      style="opacity: .6"
                    >
                      <span class="sr-only">Loading...</span>
                    </div>
                  </div>
                  <div class="text-center mt-1" v-if="!templates.length && !loadingData">
                    <p>No Templates Found...</p>
                  </div>
                  <div class="table-responsive" v-if="templates.length">
                    <table class="table zero-configuration" :aria-label="$t('contracts_aria_label_contract_template_list')">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Last edited by</th>
                          <th>Country</th>
                          <th>Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(template, index) in templates" :key="index">
                          <td class="templateName capitalize">
                            {{ template.details.en.name }}
                            <br />
                            <small>Last edit: {{ template.lastEditedAt.split('T')[0] }}</small>
                            <br />
                            <a
                              href="#"
                              @click="editTemplateModal(template.uid)"
                              style="margin-top: 5px;"
                            >
                              <em class="bx bx-edit-alt"></em> Edit
                            </a>
                          </td>
                          <td v-if="template.type === 'standard_residential'">Standard Residential Lease</td>
                          <td v-if="template.type === 'free_occupancy'">Occupancy Free of Charge Lease</td>
                          <td v-if="template.type === 'rent_reduction'">Rent Reduction Lease</td>
                          <td class="capitalize">
                            <span v-if="template.status == 'draft'">
                              <em class="bx bxs-circle light-secondary font-small-1 mr-50"></em>
                              {{ template.status }}
                            </span>
                            <span v-if="template.status == 'published'">
                              <em class="bx bxs-circle success font-small-1 mr-50"></em>
                              {{ template.status }}
                            </span>
                            <span v-if="template.status == 'archived'">
                              <em class="bx bxs-circle warning font-small-1 mr-50"></em>
                              {{ template.status }}
                            </span>
                          </td>
                          <td>
                            {{ (template.lastEditedByUser && template.lastEditedByUser.name) ? template.lastEditedByUser.name : '-' }}
                          </td>
                          <td>{{ countryName(template.country) }}</td>
                          <td>{{ template.createdAt.split('T')[0] }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Create Template Modal -->
    <div
      class="modal fade text-left"
      id="create-contract-modal"
      tabindex="-1"
      role="dialog"
      aria-labelledby="create-modal-title"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-scrollable modal-full" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h3 class="modal-title" id="create-modal-title">Create Template</h3>
            <button
              type="button"
              class="close rounded-pill"
              data-dismiss="modal"
              aria-label="Close"
            >
              <em class="bx bx-x"></em>
            </button>
          </div>
          <div class="modal-body">
            <Alert
              v-if="error"
              text="Please fill out all <strong>required</strong> fields"
              type="danger"
              dismissable="false"
            />
            <form action="#">
              <div class="row">
                <div class="col-md-6 col-sm-12">
                  <label>Title (EN):</label>
                  <div class="form-group">
                    <input
                      v-model="contractTitle"
                      type="text"
                      placeholder="Contract Title"
                      class="form-control"
                    />
                  </div>
                </div>
                <div class="col-md-6 col-sm-12">
                  <label>Title (AR):</label>
                  <div class="form-group">
                    <input
                      v-model="contractTitleAr"
                      type="text"
                      placeholder="Contract Title"
                      class="form-control text-right"
                      dir="rtl"
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6 col-sm-12">
                  <label>Country:</label>
                  <div class="form-group" v-if="isSuperadmin">
                    <select v-model="contractCountry" id class="form-control">
                      <option value="Choose..." selected disabled>Choose...</option>
                      <option
                        v-for="(country, index) in countries"
                        :key="index"
                        :value="country.toLowerCase()"
                      >{{ countryName(country) }}</option>
                    </select>
                  </div>
                  <div class="form-group" v-else>
                    <select v-model="contractCountry" id class="form-control" disabled>
                      <option value="Choose..." selected disabled>Choose...</option>
                      <option
                        v-for="(country, index) in countries"
                        :key="index"
                        :value="country.toLowerCase()"
                      >{{ countryName(country) }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6 col-sm-12">
                  <label>Type:</label>
                  <div class="form-group">
                    <select v-model="contractType" id class="form-control">
                      <option value="free_occupancy">Occupancy Free of Charge Lease</option>
                      <option value="standard_residential">Standard Residential Lease</option>
                      <option value="rent_reduction">Rent Reduction Lease</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6 col-sm-12">
                  <label for>Description (EN)</label>
                  <textarea cols="30" rows="7" class="form-control" v-model="contractDescription"></textarea>
                </div>
                <div class="col-md-6 col-sm-12">
                  <label for>Description (AR)</label>
                  <textarea dir="rtl" cols="30" rows="7" class="form-control text-right" v-model="contractDescriptionAr"></textarea>
                </div>
              </div>
            </form>
            <!-- <div class="text-left my-2">
              <h4 class="text-bold-600">Simplified Terms and Conditions</h4>
              <small>
                Write some topics to help the user understand
                <br />the terms and conditions of the contract.
              </small>
            </div>
            <form class="form repeater-default">
              <div data-repeater-list="list-terms-and-conditions">
                <div data-repeater-item>
                  <div class="row">
                    <div class="col-6">
                      <div class="row justify-content-between">
                        <div class="col-md-8 col-sm-8 form-group">
                          <label for>Term Title (EN)</label>
                          <input
                            type="text"
                            class="form-control term-title-en"
                            placeholder="Enter term title"
                          />
                        </div>
                        <div class="col-md-12 col-sm-12 form-group">
                          <label for="password">Term Description (EN)</label>
                          <textarea
                            cols="30"
                            rows="5"
                            class="form-control term-description-en"
                            placeholder="Enter term description..."
                          ></textarea>
                        </div>
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="row justify-content-between">
                        <div class="col-md-8 col-sm-8 form-group">
                          <label for>Term Title (AR)</label>
                          <input
                            type="text"
                            class="form-control term-title-ar"
                            placeholder="Enter term title"
                          />
                        </div>
                        <div class="col-md-2 col-sm-2">
                          <label for></label>
                          <button class="btn btn-default" type="button" data-repeater-delete>
                            <em class="bx bx-x" style="font-size: 1.5em; margin-top: -10px;"></em>
                          </button>
                        </div>
                        <div class="col-md-12 col-sm-12 form-group">
                          <label for="password">Term Description (AR)</label>
                          <textarea
                            cols="30"
                            rows="5"
                            class="form-control term-description-ar"
                            placeholder="Enter term description..."
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr />
                </div>
              </div>
              <div class="form-group">
                <div class="col p-0">
                  <button class="btn btn-primary" data-repeater-create type="button">Add new Term</button>
                </div>
              </div>
            </form> -->
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light-secondary" data-dismiss="modal">
              <em class="bx bx-x d-block d-sm-none"></em>
              <span class="d-none d-sm-block">Close</span>
            </button>
            <button
              type="button"
              class="btn btn-primary ml-1 create-template-btn"
              @click="createContract()"
            >
              <em class="bx bx-check d-block d-sm-none"></em>
              <span class="d-none d-sm-block">Create</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Template Modal -->
    <div
      class="modal fade text-left"
      id="edit-contract-modal"
      tabindex="-1"
      role="dialog"
      aria-labelledby="create-modal-title"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-scrollable modal-full" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h3 class="modal-title" id="create-modal-title">Edit Template</h3>
            <button
              type="button"
              class="close rounded-pill"
              data-dismiss="modal"
              aria-label="Close"
            >
              <em class="bx bx-x"></em>
            </button>
          </div>
          <div class="modal-body">
            <Alert
              v-if="errorText != ''"
              :text="errorText"
              type="danger"
              dismissable="false"
            />
            <Alert
              v-if="errorEdit"
              text="Please fill out all <strong>required</strong> fields"
              type="danger"
              dismissable="false"
            />
            <Alert
              v-if="errorAr"
              text="You need to insert all data in <strong>English</strong> and <strong>Arabic</strong>"
              type="danger"
              dismissable="false"
            />
            <Alert
              v-if="errorTerms"
              text="You need to insert Terms and Conditions in <strong>English</strong> and <strong>Arabic</strong>"
              type="danger"
              dismissable="false"
            />
            <form action="#">
              <div class="row" style="margin-top: 15px;">
                <div class="col-md-6 col-sm-12">
                  <label>Status:</label>
                  <div class="form-group">
                    <select class="form-control" v-model="contractStatusEdit">
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6 col-sm-12">
                  <label>Title (EN):</label>
                  <div class="form-group">
                    <input
                      v-model="contractTitleEdit"
                      type="text"
                      placeholder="Contract Title"
                      class="form-control"
                    />
                  </div>
                </div>
                <div class="col-md-6 col-sm-12">
                  <label>Title (AR):</label>
                  <div class="form-group">
                    <input
                      v-model="contractTitleEditAr"
                      type="text"
                      placeholder="Contract Title"
                      class="form-control text-right"
                      dir="rtl"
                    />
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6 col-sm-12">
                  <label>Country:</label>
                  <div class="form-group" v-if="isSuperadmin">
                    <select v-model="contractCountryEdit" id class="form-control">
                      <option value="Choose..." selected disabled>Choose...</option>
                      <option
                        v-for="(country, index) in countries"
                        :key="index"
                        :value="country"
                      >{{ countryName(country) }}</option>
                    </select>
                  </div>
                  <div class="form-group" v-else>
                    <select v-model="contractCountryEdit" id class="form-control" disabled>
                      <option value="Choose..." selected disabled>Choose...</option>
                      <option
                        v-for="(country, index) in countries"
                        :key="index"
                        :value="country"
                      >{{ countryName(country) }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6 col-sm-12">
                  <label>Type:</label>
                  <div class="form-group">
                    <select v-model="contractTypeEdit" id class="form-control">
                      <option value="free_occupancy">Occupancy Free of Charge Lease</option>
                      <option value="standard_residential">Standard Residential Lease</option>
                      <option value="rent_reduction">Rent Reduction Lease</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6 col-sm-12">
                  <label for>Description (EN)</label>
                  <textarea
                    cols="30"
                    rows="7"
                    class="form-control"
                    v-model="contractDescriptionEdit"
                  ></textarea>
                </div>
                <div class="col-md-6 col-sm-12">
                  <label for>Description (AR)</label>
                  <textarea
                    cols="30"
                    rows="7"
                    class="form-control text-right"
                    v-model="contractDescriptionEditAr"
                    dir="rtl"
                  ></textarea>
                </div>
              </div>
              <p class="text-muted mt-1">
                Last edited by:
                <strong>{{ lastEditedBy }}</strong>
              </p>
            </form>
            <button class="btn btn-primary" @click="downloadPDF(target)" v-if="target && target.docusignTemplate && target.docusignTemplate.documents && target.docusignTemplate.documents[0]">Download PDF</button>
            <button class="btn btn-primary" @click="generateEditView()">Edit DocuSign Template</button>
            <button class="btn btn-danger" @click="deleteTemplate(target.uid)">Delete Template</button>
            <div class="text-left my-2">
              <h4 class="text-bold-600">Simplified Terms and Conditions</h4>
              <small>
                Write some topics to help the user understand
                <br />the terms and conditions of the contract.
              </small>
            </div>
            <form class="form repeater-default-edit">
              <div data-repeater-list="list-terms-and-conditions-edit">
                <div data-repeater-item v-for="(term, index) in terms" :key="index">
                  <div class="row">
                    <div class="col-6">
                      <div class="row justify-content-between">
                        <div class="col-md-8 col-sm-8 form-group">
                          <label for>Term Title (EN)</label>
                          <input
                            type="text"
                            class="form-control term-title-edit"
                            placeholder="Enter term title"
                            :value="term.title"
                          />
                        </div>
                        <div class="col-md-12 col-sm-12 form-group">
                          <label for="password">Term Description (EN)</label>
                          <textarea
                            cols="30"
                            rows="5"
                            class="form-control term-description-edit"
                            placeholder="Enter term description..."
                            :value="term.description"
                          ></textarea>
                        </div>
                      </div>
                    </div>

                    <div class="col-6">
                      <div class="row justify-content-between">
                        <div class="col-md-8 col-sm-8 form-group">
                          <label for>Term Title (AR)</label>
                          <input
                            type="text"
                            class="form-control term-title-edit-ar text-right"
                            placeholder="Enter term title"
                            :value="termsAr[index].title"
                            dir="rtl"
                          />
                        </div>
                        <div class="col-md-2 col-sm-2">
                          <label for></label>
                          <button class="btn btn-default" type="button" data-repeater-delete>
                            <em class="bx bx-x" style="font-size: 1.5em; margin-top: -10px;"></em>
                          </button>
                        </div>
                        <div class="col-md-12 col-sm-12 form-group">
                          <label for="password">Term Description (AR)</label>
                          <textarea
                            cols="30"
                            rows="5"
                            class="form-control term-description-edit-ar text-right"
                            placeholder="Enter term description..."
                            :value="termsAr[index].description"
                            dir="rtl"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr />
                </div>
              </div>
            </form>
            <form class="form repeater-default-new">
              <div data-repeater-list="list-terms-and-conditions-new">
                <div data-repeater-item>
                  <div class="row">
                    <div class="col-6">
                      <div class="row justify-content-between">
                        <div class="col-md-8 col-sm-8 form-group">
                          <label for>Term Title (EN)</label>
                          <input
                            type="text"
                            class="form-control term-title-edit"
                            placeholder="Enter term title"
                          />
                        </div>
                        <div class="col-md-12 col-sm-12 form-group">
                          <label for="password">Term Description (EN)</label>
                          <textarea
                            cols="30"
                            rows="5"
                            class="form-control term-description-edit"
                            placeholder="Enter term description..."
                          ></textarea>
                        </div>
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="row justify-content-between">
                        <div class="col-md-8 col-sm-8 form-group">
                          <label for>Term Title (AR)</label>
                          <input
                            type="text"
                            class="form-control term-title-edit-ar text-right"
                            placeholder="Enter term title"
                            dir="rtl"
                          />
                        </div>
                        <div class="col-md-2 col-sm-2">
                          <label for></label>
                          <button class="btn btn-default" type="button" data-repeater-delete>
                            <em class="bx bx-x" style="font-size: 1.5em; margin-top: -10px;"></em>
                          </button>
                        </div>
                        <div class="col-md-12 col-sm-12 form-group">
                          <label for="password">Term Description (AR)</label>
                          <textarea
                            cols="30"
                            rows="5"
                            class="form-control term-description-edit-ar text-right"
                            placeholder="Enter term description..."
                            dir="rtl"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr />
                </div>
              </div>
              <div class="form-group">
                <div class="col p-0">
                  <button class="btn btn-primary" data-repeater-create type="button">Add new Term</button>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light-secondary" data-dismiss="modal">
              <em class="bx bx-x d-block d-sm-none"></em>
              <span class="d-none d-sm-block" @click="closeModal()">Close</span>
            </button>
            <button type="button" class="btn btn-primary ml-1" @click="editTemplate()">
              <em class="bx bx-check d-block d-sm-none"></em>
              <span class="d-none d-sm-block">Save</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCountryName } from "@/app-assets/js/get-countries.js";
import axios from "axios";
import "@/app-assets/js/scripts/extensions/ext-component-block-ui.js";
import Alert from "@/components/Alerts";

/** Page CSS */
import "@/app-assets/vendors/css/tables/datatable/datatables.min.css";
import "@/app-assets/vendors/css/tables/datatable/extensions/dataTables.checkboxes.css";
import "@/app-assets/vendors/css/tables/datatable/responsive.bootstrap.min.css";
import "@/app-assets/css/pages/app-invoice.css";

export default {
  data() {
    return {
      templates: [],
      countries: [],
      target: [],
      usersCountries: [],
      loadingData: true,
      contractTitle: "",
      contractTitleAr: "",
      contractCountry: "Choose...",
      countryFilter: "All Countries...",
      statusFilter: "All Status...",
      contractType: "free_occupancy",
      contractDescription: "",
      contractDescriptionAr: "",
      contractTitleEdit: "",
      contractTitleEditAr: "",
      contractCountryEdit: "",
      contractStatusEdit: "",
      contractTypeEdit: "",
      contractDescriptionEdit: "",
      contractDescriptionEditAr: "",
      lastEditedBy: "-",
      error: false,
      errorEdit: false,
      errorAr: false,
      errorTerms: false,
      terms: [],
      termsAr: [],
      errorText: "",

      valueInterval: null
    };
  },

  components: {
    Alert,
  },

  computed: {
    isSuperadmin() {
      return this.$store.state.currentUser.user.type === "superadmin";
    },
  },

  methods: {

    downloadPDF(template) {
      var block_ele = $("#edit-contract-modal .modal-content");
      $(block_ele).block({
        message:
          '<div class="bx bx-revision icon-spin font-medium-2"></div>',
        overlayCSS: {
          backgroundColor: "#fff",
          opacity: 0.8,
          cursor: "wait",
        },
        css: {
          border: 0,
          padding: 0,
          backgroundColor: "transparent",
        },
      });
      this.errorText = "";
      this.$apiCall.get(`/v1/contracts/templates/${template.uid}/download?documentId=${template.docusignTemplate.documents[0].documentId}`, {
        responseType: 'blob'
      })
      .then(res => {
        const file = new Blob([res.data], {type: 'application/pdf'});
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
        $(block_ele).unblock();
      })
      .catch(error => {
        this.errorText = `<strong>Ops!</strong> ${error.response.data.message}`;
        $(block_ele).unblock();
      })
    },

    createContractModal() {
      $("#create-contract-modal").modal();
    },

    createContractDocusign(terms, details) {
      let self = this;
      this.errorText = "";
      $.blockUI({
        message: '<div class="bx bx-revision icon-spin font-medium-2"></div>',
        overlayCSS: {
          backgroundColor: "#fff",
          opacity: 0.8,
          cursor: "wait",
        },
        css: {
          border: 0,
          padding: 0,
          backgroundColor: "transparent",
        },
      });

      self.$apiCall
        .post("/v1/contracts/templates", {
          details: details,
          country: self.contractCountry,
          type: self.contractType,
          terms: terms,
        })
        .then(response => {
          $(".term-title").val("");
          $(".term-description").val("");
          $.unblockUI();
          $("#create-contract-modal").modal("hide");
          self.templates = [];
          self.getTemplatesDocusign();
          self.contractTitle = "";
          self.contractTitleAr="",
          self.contractDescriptionAr="",
          self.contractCountry = "Choose...";
          self.contractType = "free_occupancy";
          self.contractDescription = "";
          self.editTemplateModal(response.data.uid);
        })
        .catch((error) => {
          this.errorText = `<strong>Ops!</strong> ${error.response.data.message}`;
          $.unblockUI();
        });
    },
    closeModal(){
      this.errorTerms = false;
      this.errorAr = false;
    },

    createContract() {
      this.error = false;

      let termTitlesEn = [];
      let termDescriptionsEn = [];
      let termTitlesAr = [];
      let termDescriptionsAr = [];

      let details = {
        en: {
          name: this.contractTitle,
          description: this.contractDescription,
        },
        ar: {
          name: this.contractTitleAr,
          description: this.contractDescriptionAr,
        },
      };

      let terms = { en: [], ar: [] };

      $(".term-title-en").each(function (e) {
        termTitlesEn.push($(this).val());
      });

      $(".term-description-en").each(function (e) {
        termDescriptionsEn.push($(this).val());
      });

      $(".term-title-ar").each(function (e) {
        termTitlesAr.push($(this).val());
      });

      $(".term-description-ar").each(function (e) {
        termDescriptionsAr.push($(this).val());
      });

      termTitlesEn.forEach((element, i) => {
        if (element.trim() != "" && termDescriptionsEn[i].trim() != "") {
          let term = {
            title: element,
            description: termDescriptionsEn[i],
          };

          terms.en.push(term);
        }
      });

      termTitlesAr.forEach((element, i) => {
        if (element.trim() != "" && termDescriptionsAr[i].trim() != "") {
          let term = {
            title: element,
            description: termDescriptionsAr[i],
          };

          terms.ar.push(term);
        }
      });

      // EN to AR check
      terms.en.forEach((element, i) => {
        if (!terms.ar[i]) {
          terms.ar[i] = {
            title: "",
            description: "",
          };
        }
      });

      // AR to EN check
      terms.ar.forEach((element, i) => {
        if (!terms.en[i]) {
          terms.en[i] = {
            title: "",
            description: "",
          };
        }
      });

      if (
        this.contractTitle != "" &&
        this.contractDescription != "" &&
        this.contractDescriptionAr != "" &&
        this.contractTitleAr !="" &&
        this.contractCountry != "Choose..."
      ) {
        this.createContractDocusign(terms, details);
      } else {
        this.error = true;
      }
    },

    deleteTemplate(uid) {
      let self = this;
      this.errorText = '';
      $.blockUI({
        message: '<div class="bx bx-revision icon-spin font-medium-2"></div>',
        overlayCSS: {
          backgroundColor: "#fff",
          opacity: 0.8,
          cursor: "wait",
        },
        css: {
          border: 0,
          padding: 0,
          backgroundColor: "transparent",
        },
      });
      this.$apiCall.delete(`/v1/contracts/templates/${uid}`)
      .then(() => {
        $.unblockUI();
        $("#edit-contract-modal").modal("hide");
        self.templates = [];
        self.getTemplatesDocusign();
      })
      .catch((error) => {
        this.errorText = `<strong>Ops!</strong> ${error.response.data.message}`;
        $.unblockUI();
      });
    },

    getTemplatesDocusign() {
      let self = this;
      self.loadingData = true;
      self.templates = [];
      this.$apiCall
        .get("/v1/contracts/templates")
        .then((response) => {
          var templates = [];
          let url = [];

          response.data.data.forEach(function (element) {
            if (
              !self.usersCountries.includes(element.country) &&
              element.country
            ) {
              self.usersCountries.push(element.country);
            }

            url.push(element)
          });
            self.loadingData = false;
            self.templates = url;
            self.initDataTable();

        });
    },

    filterByCountry(country) {
      let self = this;
      if (country != "All Countries...") {
        self.loadingData = true;
        self.templates = [];
        self.$apiCall
          .get(`/v1/contracts/templates?country=${country}`)
          .then((response) => {
            self.loadingData = false;
            self.templates = response.data.data;
            self.initDataTable();
          });
      } else {
        self.templates = [];
        self.usersCountries = [];
        self.getTemplatesDocusign();
      }
    },

    filterByStatus(status) {
      let self = this;
      if (status != "All Status...") {
        self.loadingData = true;
        self.templates = [];
        self.$apiCall
          .get(
            `/v1/contracts/templates?status=${status.toLowerCase()}`
          )
          .then((response) => {
            self.loadingData = false;
            self.templates = response.data.data;
            self.initDataTable();
          });
      } else {
        self.templates = [];
        self.usersCountries = [];
        self.getTemplatesDocusign();
      }
    },

    editTemplateModal(uid) {
      this.errorEdit = false;
      let self = this;
      $.blockUI({
        message: '<div class="bx bx-revision icon-spin font-medium-2"></div>',
        overlayCSS: {
          backgroundColor: "#fff",
          opacity: 0.8,
          cursor: "wait",
        },
        css: {
          border: 0,
          padding: 0,
          backgroundColor: "transparent",
        },
      });
      this.$apiCall.get(`/v1/contracts/templates/${uid}`).then((response) => {
        $.unblockUI();
        self.target = response.data;
        self.terms = response.data.terms.en;
        self.termsAr = response.data.terms.ar;
        self.contractTitleEdit = response.data.details.en.name;
        self.contractTitleEditAr = response.data.details.ar
          ? response.data.details.ar.name
          : "";
        self.contractDescriptionEdit = response.data.details.en.description;
        self.contractDescriptionEditAr = response.data.details.ar
          ? response.data.details.ar.description
          : "";
        self.contractTypeEdit = response.data.type;
        self.contractCountryEdit = response.data.country.toUpperCase();
        self.contractStatusEdit = response.data.status;
        self.lastEditedBy = self.target.lastEditedByUser.name
          ? self.target.lastEditedByUser.name
          : "-";
        $("#edit-contract-modal").modal();
      });
    },

    checkTermsAndConditions(terms) {
      let okTermsEn = true;
      let okTermsAr = true;

      terms.en.forEach((element, i) => {
        if (element.title.trim() == "" || element.description.trim() == "") {
          okTermsEn = false;
        }
      });

      terms.ar.forEach((element, i) => {
        if (element.title.trim() == "" || element.description.trim() == "") {
          okTermsAr = false;
        }
      });

      return okTermsAr && okTermsEn;
    },

    publishTemplate(terms, details) {
      let self = this;
      this.errorTerms = false;

      if (!terms.en.length || !terms.ar.length) {
        this.errorTerms = true;
      } else {
        let canPublish = this.checkTermsAndConditions(terms);

        if (canPublish) {
          var block_ele = $("#edit-contract-modal .modal-content");
          $(block_ele).block({
            message:
              '<div class="bx bx-revision icon-spin font-medium-2"></div>',
            overlayCSS: {
              backgroundColor: "#fff",
              opacity: 0.8,
              cursor: "wait",
            },
            css: {
              border: 0,
              padding: 0,
              backgroundColor: "transparent",
            },
          });

          this.$apiCall
            .patch(`/v1/contracts/templates/${self.target.uid}`, {
              details: details,
              docusignId: self.target.docusignId,
              country: self.contractCountryEdit.toLowerCase(),
              type: self.contractTypeEdit,
              terms: terms,
              status: self.contractStatusEdit,
            })
            .then(() => {
              $(".term-title-edit").val("");
              $(".term-description-edit").val("");
              $(".term-title-edit-ar").val("");
              $(".term-description-edit-ar").val("");
              $(block_ele).unblock();
              $("#edit-contract-modal").modal("hide");
              self.templates = [];
              self.getTemplatesDocusign();
            });
        } else {
          this.errorTerms = true;
        }
      }
    },

    editTemplate() {
      this.errorText = "";
      let termTitlesEn = [];
      let termDescriptionsEn = [];
      let termTitlesAr = [];
      let termDescriptionsAr = [];

      let details = {
        en: {
          name: this.contractTitleEdit,
          description: this.contractDescriptionEdit,
        },
        ar: {
          name: this.contractTitleEditAr,
          description: this.contractDescriptionEditAr,
        },
      };

      let terms = { en: [], ar: [] };

      $(".term-title-edit").each(function (e) {
        termTitlesEn.push($(this).val());
      });

      $(".term-description-edit").each(function (e) {
        termDescriptionsEn.push($(this).val());
      });

      $(".term-title-edit-ar").each(function (e) {
        termTitlesAr.push($(this).val());
      });

      $(".term-description-edit-ar").each(function (e) {
        termDescriptionsAr.push($(this).val());
      });

      termTitlesEn.forEach((element, i) => {
        if (element.trim() != "" && termDescriptionsEn[i].trim() != "") {
          let term = {
            title: element,
            description: termDescriptionsEn[i],
          };

          terms.en.push(term);
        }
      });

      termTitlesAr.forEach((element, i) => {
        if (element.trim() != "" && termDescriptionsAr[i].trim() != "") {
          let term = {
            title: element,
            description: termDescriptionsAr[i],
          };

          terms.ar.push(term);
        }
      });

      // EN to AR check
      terms.en.forEach((element, i) => {
        if (!terms.ar[i]) {
          terms.ar[i] = {
            title: "",
            description: "",
          };
        }
      });

      // AR to EN check
      terms.ar.forEach((element, i) => {
        if (!terms.en[i]) {
          terms.en[i] = {
            title: "",
            description: "",
          };
        }
      });

      let self = this;
      self.errorEdit = false;
      self.errorAr = false;

      if (
        this.contractTitleEdit.trim() != "" &&
        this.contractDescriptionEdit.trim() != "" &&
        this.contractCountryEdit != "Choose..."
      ) {
        if (this.contractStatusEdit == "published") {
          if (
            this.contractDescriptionEditAr.trim() == "" ||
            this.contractTitleEditAr.trim() == ""
          ) {
            this.errorAr = true;
          } else {
            this.publishTemplate(terms, details);
          }
        } else {
          var block_ele = $("#edit-contract-modal .modal-content");
          $(block_ele).block({
            message:
              '<div class="bx bx-revision icon-spin font-medium-2"></div>',
            overlayCSS: {
              backgroundColor: "#fff",
              opacity: 0.8,
              cursor: "wait",
            },
            css: {
              border: 0,
              padding: 0,
              backgroundColor: "transparent",
            },
          });

          this.$apiCall
            .patch(`/v1/contracts/templates/${self.target.uid}`, {
              details: details,
              country: self.contractCountryEdit.toLowerCase(),
              type: self.contractTypeEdit,
              terms: terms,
              status: self.contractStatusEdit,
            })
            .then(() => {
              $(".term-title-edit").val("");
              $(".term-description-edit").val("");
              $(".term-title-edit-ar").val("");
              $(".term-description-edit-ar").val("");
              $(block_ele).unblock();
              $("#edit-contract-modal").modal("hide");
              self.templates = [];
              self.getTemplatesDocusign();
            })
            .catch(error => {
              this.errorText = `<strong>Ops!</strong> ${error.response.data.message}`;
            })
        }
      } else {
        self.errorEdit = true;
      }
    },

    generateEditView() {
      let self = this;
      let envelopeId = self.target.docusignId;
      var block_ele = $("#edit-contract-modal .modal-content");
      $(block_ele).block({
        message: '<div class="bx bx-revision icon-spin font-medium-2"></div>',
        overlayCSS: {
          backgroundColor: "#fff",
          opacity: 0.8,
          cursor: "wait",
        },
        css: {
          border: 0,
          padding: 0,
          backgroundColor: "transparent",
        },
      });

      this.$apiCall.get(`/v1/contracts/templates/${self.target.uid}/edit`)
      .then((response) => {
        $(block_ele).unblock();
        $("#edit-contract-modal").modal("hide");
        window.open(response.data, "_blank");
      })
      .catch(error => {
        console.log(error);
      })
    },

    getActiveCountries() {
      const countries = localStorage.getItem('activeCountries');
      this.countries = (countries && countries != '') ? countries.split(',') : ["LB", "JO"];
    },

    countryName(element) {
      if (element) {
        return getCountryName(element.toUpperCase());
      }
    },

    initDataTable() {
      let self = this;
      setTimeout(function () {
        $(".zero-configuration").DataTable({
          bLengthChange: false,
          bFilter: false,
          order: [],
        });
      }, 100);
    },

    loadFormPlugin() {
      $(
        ".file-repeater, .contact-repeater, .repeater-default, .repeater-default-edit, .repeater-default-new"
      ).repeater({
        show: function () {
          $(this).slideDown();
        },
        hide: function (deleteElement) {
          $(this).slideUp(deleteElement);
        },
      });
    },

    handleTermsValue() {
      $(".term-title-edit").each(function(e){
        if($(this).val().trim() === ''){
          $(this).addClass("validation-error");
        } else{
          $(this).removeClass("validation-error");
        }
      })

      $(".term-title-edit-ar").each(function(e){
        if($(this).val().trim() === ''){
          $(this).addClass("validation-error");
        } else{
          $(this).removeClass("validation-error");
        }
      })

      $(".term-description-edit").each(function(e){
        if($(this).val().trim() === ''){
          $(this).addClass("validation-error");
        } else{
          $(this).removeClass("validation-error");
        }
      })

      $(".term-description-edit-ar").each(function(e){
        if($(this).val().trim() === ''){
          $(this).addClass("validation-error");
        } else{
          $(this).removeClass("validation-error");
        }
      })
    }
  },

  mounted() {
    this.getTemplatesDocusign();
    this.getActiveCountries();
    this.loadFormPlugin();
    this.handleTermsValue();
    this.valueInterval = setInterval(() => {
      this.handleTermsValue();
    }, 1000)

    if (this.$store.state.currentUser.user.type == "superadmin") {
      this.contractCountry = "Choose...";
    } else {
      this.contractCountry = this.$store.state.currentUser.user.country;
    }

    if (navigator.appVersion.indexOf("Chrome/") != -1) {
        $("li > .btn.btn-primary").addClass("mt-chrome")
    } else {
        $("li > .btn.btn-primary").addClass("mt-firefox")
    }
  },

  destroyed() {
    clearInterval(this.valueInterval);
  }
};
</script>

<style scoped>
.zero-configuration td {
  white-space: nowrap;
}

.zero-configuration th {
  white-space: nowrap;
}

.validation-error{
  border: solid 1px red !important;
}
.heading-elements .list-inline li {
  margin-right: 5px;
}
</style>
